import { generateParams } from 'features/filters/filtersSlice';

import { get, post, put } from 'utils/api/api';

import { ApplicationsList } from 'types/applications';
import { User } from 'types/auth';
import {
  BulkActionResultItem,
  Education,
  EducationAndWorkExperience,
  WorkExperience
} from 'types/commons';
import { Filters } from 'types/filters';
import {
  CreateJobseeker,
  Jobseeker,
  JobseekerFacet,
  JobseekersList,
  JobseekersMatches,
  JobseekersPrivateDocuments,
  UpdatePrivateDocument
} from 'types/jobseekers';

import {
  AMBASSADOR,
  APPLICATION,
  EDUCATION,
  EDUCATION_AND_WORK_EXPERIENCE,
  INVITE,
  JOBSEEKER,
  JOBSEEKERS,
  JOBSEEKERS_CONTACT_DATE_BULK,
  JOBSEEKERS_FACET,
  JOBSEEKERS_IDS,
  JOBSEEKERS_IMMIGRATION_ROUTE_BULK,
  JOBSEEKERS_INVITE_BULK,
  JOBSEEKERS_RECRUITER_BULK,
  JOBSEEKERS_STAGE_BULK,
  JOBSEEKER_APPLICATIONS,
  JOB_PART,
  MATCHES,
  RESOURCE,
  WORK_EXPERIENCE
} from 'constants/endpoints';
import { ApplicationStage, JobseekerStage } from 'constants/enums';

class JobseekersService {
  async getJobseekers(params: string): Promise<JobseekersList> {
    const response = await get(`${JOBSEEKERS}${params}`);
    return response;
  }

  async getJobseekersFacet(params: string): Promise<JobseekerFacet[]> {
    const response = await get(`${JOBSEEKERS_FACET}${params}`);
    return response;
  }

  async getJobseekerById(id: string): Promise<Jobseeker> {
    const response = await get(`${JOBSEEKER}/${id}`);
    return response;
  }

  async getJobseekerWorkExperience(id: string): Promise<WorkExperience | null> {
    const response = await get(`${JOBSEEKER}${WORK_EXPERIENCE}/${id}`);
    return response;
  }

  async getJobseekerEducation(id: string): Promise<Education | null> {
    const response = await get(`${JOBSEEKER}${EDUCATION}/${id}`);
    return response;
  }

  async getJobseekerEducationAndWorkExperience(
    id: string
  ): Promise<EducationAndWorkExperience | null> {
    const response = await get(
      `${JOBSEEKER}${EDUCATION_AND_WORK_EXPERIENCE}/${id}`
    );
    return response;
  }

  async getJobseekersMatchesJob(
    jobId: string,
    queryParams?: string
  ): Promise<JobseekersMatches | null> {
    const response = await get(
      `${JOBSEEKER}${JOB_PART}/${jobId}${MATCHES}${queryParams ?? ''}`
    );
    return response;
  }

  async changeJobseekerStageAndTags(
    id: string,
    value: JobseekerStage,
    tags: string[]
  ): Promise<void> {
    await put(`${JOBSEEKER}/${id}/stage/${value}/tags`, tags);
  }

  async changeJobseekerApplicationStage(
    id: string,
    stage: ApplicationStage
  ): Promise<void> {
    await put(`${APPLICATION}/${id}/stage/${stage}`, {});
  }

  async changeJobseekerImmigrationRoute(
    id: string,
    route: string
  ): Promise<void> {
    await put(`${JOBSEEKER}/${id}/immigrationRoutes`, [route]);
  }

  async createNewJobseeker(data: CreateJobseeker): Promise<void> {
    await post(JOBSEEKER, data);
  }

  async changeJobseekerContactDate(id: string, date: string): Promise<void> {
    await put(`${JOBSEEKER}/${id}/contactDate/${date}`, {});
  }

  async assignRecruiters(id: string, recruitersIds: string[]): Promise<void> {
    const recruiters = recruitersIds.map((id) => ({ id }));
    await put(`${JOBSEEKER}/${id}/recruiter`, { recruiters });
  }

  async assignAceContact(id: string, user: User): Promise<void> {
    await put(`${JOBSEEKER}/${id}/ace_contact`, user);
  }

  async assignAmbassador(
    jobseekerId: string,
    ambassadorId: string
  ): Promise<void> {
    await put(`${AMBASSADOR}/${ambassadorId}/jobseeker/${jobseekerId}`, {});
  }

  async getJobseekerApplications(
    id: string,
    filters: Filters
  ): Promise<ApplicationsList> {
    const params = generateParams(filters);
    const response = await get(`${JOBSEEKER_APPLICATIONS}/${id}${params}`);
    return response;
  }

  async updateJobseeker(
    id: string,
    data: Partial<Jobseeker>
  ): Promise<Jobseeker> {
    const response = await put(`${JOBSEEKER}/${id}`, data);
    return response;
  }

  async updatePrivateDocumentPermission(
    id: string,
    data: UpdatePrivateDocument
  ): Promise<JobseekersPrivateDocuments> {
    const response = await put(`${RESOURCE}/${id}`, data);
    return response;
  }

  async getJobseekersIds(filters: Filters): Promise<string[]> {
    const params = generateParams(filters);
    const response = await get(`${JOBSEEKERS_IDS}${params}`);
    return response || [];
  }

  async updateJobseekersStageBulk(
    ids: string[],
    stage: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(JOBSEEKERS_STAGE_BULK, {
      ids,
      stage
    });
    return response;
  }

  async updateJobseekersRecruiterBulk(
    ids: string[],
    recruiterId: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(JOBSEEKERS_RECRUITER_BULK, {
      ids,
      recruiters: [{ id: recruiterId }]
    });
    return response;
  }

  async updateJobseekersImmigrationRouteBulk(
    ids: string[],
    immigrationRoute: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(JOBSEEKERS_IMMIGRATION_ROUTE_BULK, {
      ids,
      immigrationRoutes: [immigrationRoute]
    });
    return response;
  }

  async updateJobseekersContactDateBulk(
    ids: string[],
    contactDate: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(JOBSEEKERS_CONTACT_DATE_BULK, {
      ids,
      contactDate
    });
    return response;
  }

  async sendJobInviteToJobseeker(
    jobseekerId: string,
    jobId: string
  ): Promise<void> {
    await post(`${JOBSEEKER}/${jobseekerId}${INVITE}/${jobId}`, {});
  }

  async sendJobInviteToJobseekerBulk(
    ids: string[],
    jobId: string
  ): Promise<BulkActionResultItem[]> {
    const response = await post(`${JOBSEEKERS_INVITE_BULK}/${jobId}`, { ids });
    return response;
  }
}

export default new JobseekersService();
